'use client';

import type { BlogAppLink, BlogClusterId, BlogFileAsset } from '@/lib/blog/content';
import Link from 'next/link';
import { startTransition, useDeferredValue, useEffect, useState } from 'react';

const INITIAL_VISIBLE = 24;
const LOAD_MORE_STEP = 24;

type BlogIndexCluster = {
  id: BlogClusterId;
  eyebrow: string;
  title: string;
  description: string;
  articleCount: number;
  primaryHref: string;
  primaryLabel: string;
  helperHref: string;
  helperLabel: string;
  appHref: string;
  appLabel: string;
};

type BlogIndexArticle = {
  slug: string;
  title: string;
  displayTitle: string;
  excerpt: string;
  publishedAt: string;
  keywords: string[];
  tags: string[];
  coverImage?: BlogFileAsset;
  primaryLink?: BlogAppLink;
  author: {
    slug: string;
    name: string;
  };
  cluster: {
    id: BlogClusterId;
    eyebrow: string;
    title: string;
  } | null;
};

type Props = {
  articles: BlogIndexArticle[];
  clusters: BlogIndexCluster[];
};

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value));
}

function normalizeSearchValue(value: string) {
  return value.trim().toLowerCase();
}

type BlogSortMode = 'newest' | 'oldest' | 'az';

const blogSortLabels: Record<BlogSortMode, string> = {
  newest: 'Newest',
  oldest: 'Oldest',
  az: 'A → Z',
};

// Short, tools-style chip labels. Prefer the cluster's own eyebrow (already a
// terse tag like "APPS" / "IPHONE") over the long title ("App and chat storage").
const clusterChipOverrides: Record<string, string> = {
  IPHONE: 'iPhone',
  IOS: 'iOS',
  APIS: 'APIs',
  SEO: 'SEO',
};

function clusterChipLabel(eyebrow: string, fallback: string) {
  const raw = (eyebrow || fallback).trim();
  const key = raw.toUpperCase();

  if (clusterChipOverrides[key]) {
    return clusterChipOverrides[key];
  }

  // Title-case a single short token; longer eyebrows are left as-is.
  if (!raw.includes(' ')) {
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }

  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function sortArticles(list: BlogIndexArticle[], mode: BlogSortMode) {
  const sorted = [...list];

  if (mode === 'az') {
    sorted.sort((left, right) => left.displayTitle.localeCompare(right.displayTitle));
  } else {
    sorted.sort((left, right) => {
      const delta = new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime();
      return mode === 'oldest' ? -delta : delta;
    });
  }

  return sorted;
}

function articleMatches(article: BlogIndexArticle, query: string, activeTag: string) {
  if (activeTag !== 'all' && !article.tags.includes(activeTag)) {
    return false;
  }

  if (!query) {
    return true;
  }

  const haystack = [
    article.slug.replace(/-/g, ' '),
    article.title,
    article.displayTitle,
    article.excerpt,
    article.author.name,
    article.cluster?.title ?? '',
    article.cluster?.eyebrow ?? '',
    ...article.tags,
    ...article.keywords,
  ]
    .join(' ')
    .toLowerCase();

  return haystack.includes(query);
}

function BlogResultCard({
  article,
  onTagSelect,
}: {
  article: BlogIndexArticle;
  onTagSelect: (tag: string) => void;
}) {
  return (
    <article className="blog-result-card">
      <div className="blog-result-meta">
        <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>
        {article.cluster ? <span>{article.cluster.eyebrow}</span> : null}
        <Link href={`/authors/${article.author.slug}`}>{article.author.name}</Link>
      </div>
      <div className="blog-result-copy">
        <h2>
          <Link href={`/blog/${article.slug}`}>{article.displayTitle}</Link>
        </h2>
        <p>{article.excerpt}</p>
      </div>
      <div className="blog-result-tags" aria-label="Article tags">
        {article.tags.slice(0, 4).map((tag) => (
          <button
            className="blog-tag-chip"
            key={`${article.slug}-${tag}`}
            onClick={() => onTagSelect(tag)}
            type="button"
          >
            {tag}
          </button>
        ))}
      </div>
      <div className="project-card-actions">
        <Link className="soft-link" href={`/blog/${article.slug}`}>
          Read article
        </Link>
        {article.primaryLink ? (
          <Link className="soft-link soft-link-muted" href={article.primaryLink.href}>
            {article.primaryLink.label}
          </Link>
        ) : null}
      </div>
    </article>
  );
}

export default function BlogIndexExplorer({ articles, clusters }: Props) {
  const [query, setQuery] = useState('');
  const [activeClusterId, setActiveClusterId] = useState<'all' | BlogClusterId>('all');
  const [activeTag, setActiveTag] = useState<string>('all');
  const [sortMode, setSortMode] = useState<BlogSortMode>('newest');
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = normalizeSearchValue(deferredQuery);
  const clusterScopedArticles =
    activeClusterId === 'all'
      ? articles
      : articles.filter((article) => article.cluster?.id === activeClusterId);
  const visibleArticles = sortArticles(
    clusterScopedArticles.filter((article) => articleMatches(article, normalizedQuery, activeTag)),
    sortMode,
  );
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  // Reset the visible window whenever the result set changes so we never paint
  // the full archive (463+ cards) at once — that was the main cause of slow loads.
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [activeClusterId, activeTag, normalizedQuery, sortMode]);

  const shownArticles = visibleArticles.slice(0, visibleCount);
  const hiddenCount = visibleArticles.length - shownArticles.length;

  return (
    <section className="tool-app-catalog blog-catalog">
      <div className="tool-app-catalog-toolbar">
        <label className="tool-app-catalog-search">
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none">
            <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.75" />
            <path d="M16 16l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
          <span className="sr-only">Search guides</span>
          <input
            onChange={(event) => {
              const nextValue = event.currentTarget.value;
              startTransition(() => setQuery(nextValue));
            }}
            placeholder="Search guides, devices, apps, and tags"
            type="search"
            value={query}
          />
          {query ? (
            <button
              className="tool-app-catalog-clear"
              onClick={() => {
                startTransition(() => setQuery(''));
              }}
              type="button"
            >
              Clear
            </button>
          ) : null}
        </label>

        <div className="tool-app-catalog-controls">
          <div className="tool-app-catalog-filter-row" aria-label="Blog categories">
            <button
              className={`tool-app-catalog-filter ${
                activeClusterId === 'all' ? 'tool-app-catalog-filter-active' : ''
              }`.trim()}
              onClick={() => {
                startTransition(() => {
                  setActiveClusterId('all');
                  setActiveTag('all');
                });
              }}
              type="button"
            >
              All
            </button>
            {clusters.map((cluster) => (
              <button
                className={`tool-app-catalog-filter ${
                  activeClusterId === cluster.id ? 'tool-app-catalog-filter-active' : ''
                }`.trim()}
                key={cluster.id}
                onClick={() => {
                  startTransition(() => setActiveClusterId(cluster.id));
                }}
                type="button"
              >
                {clusterChipLabel(cluster.eyebrow, cluster.title)}
              </button>
            ))}
          </div>

          <label className="tool-app-catalog-sort">
            <span>Sort</span>
            <select
              value={sortMode}
              onChange={(event) => {
                startTransition(() => setSortMode(event.currentTarget.value as BlogSortMode));
              }}
            >
              {(Object.keys(blogSortLabels) as BlogSortMode[]).map((mode) => (
                <option key={mode} value={mode}>
                  {blogSortLabels[mode]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="tool-app-catalog-status">
          <span>{visibleArticles.length}</span>
          <p>{visibleArticles.length === 1 ? 'guide' : 'guides'}</p>
        </div>
      </div>

      {shownArticles.length ? (
        <div className="tool-app-catalog-list blog-catalog-list">
          {shownArticles.map((article) => (
            <BlogResultCard
              article={article}
              key={article.slug}
              onTagSelect={(tag) => {
                startTransition(() => setActiveTag(tag));
              }}
            />
          ))}
        </div>
      ) : (
        <div className="blog-empty-state">
          <strong>No articles match the current filters.</strong>
          <p>Try a shorter query or switch categories.</p>
        </div>
      )}

      {hiddenCount > 0 ? (
        <div className="blog-load-more">
          <button
            className="button button-ghost"
            onClick={() => setVisibleCount((current) => current + LOAD_MORE_STEP)}
            type="button"
          >
            Show more guides
          </button>
          <span className="blog-load-more-note">
            Showing {shownArticles.length.toLocaleString()} of{' '}
            {visibleArticles.length.toLocaleString()}
          </span>
        </div>
      ) : null}
    </section>
  );
}
